import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { XIcon, DownloadSimpleIcon, CopyIcon, XLogoIcon, TelegramLogoIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { fetchRugcheck, trackEvent } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { stampBrandApe } from "../lib/shareLogo";

const NEON = "#00E599", DANGER = "#FF3366", WARN = "#F5A623", MUTED = "#8b8b8b";
const verdictColor = (v) => (v === "High Confidence" ? NEON : v === "Moderate Confidence" ? NEON : v === "Elevated Risk" ? WARN : v === "High Risk" ? DANGER : "#8A8F98");

const utcTime = (ts) => {
  const d = ts ? new Date(ts * 1000) : new Date();
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;
};

// Editorial "Token Autopsy" forensic card — the shareable format the newsroom voice posts.
export const TokenAutopsyCard = ({ coin, open, onClose }) => {
  const canvasRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const rugQ = useQuery({ queryKey: ["rugcheck", coin.address], queryFn: () => fetchRugcheck(coin.address), enabled: open });
  const rug = rugQ.data?.available ? rugQ.data : null;

  const shareUrl = `https://alphaapeing.com/token/${coin.address}`;
  const max = coin.score.maxPossible || 100;
  const shareText = `Token Autopsy: $${(coin.symbol || "").replace("$", "")} — Degen Score ${coin.score.total}/${max} (${coin.score.verdict}) 🦍🔬`;

  const lines = () => {
    const out = [];
    if (rug) {
      out.push({ k: "Top holder", v: rug.topHolderPct != null ? `${rug.topHolderPct}% of supply` : "unknown", ok: (rug.topHolderPct ?? 0) < 20 });
      out.push({ k: "Top 10 hold", v: rug.top10Pct != null ? `${rug.top10Pct}% of supply` : "unknown", ok: (rug.top10Pct ?? 0) < 50 });
      out.push({ k: "LP status", v: rug.lpLocked ? "locked / burned" : rug.lpUnlocked ? "UNLOCKED — rug vector" : `${rug.lpLockedPct || 0}% locked (unverified)`, ok: rug.lpLocked });
      out.push({ k: "Mint authority", v: rug.mintRevoked ? "revoked" : "ACTIVE — can inflate", ok: rug.mintRevoked });
      out.push({ k: "Freeze authority", v: rug.freezeRevoked ? "revoked" : "ACTIVE — can freeze", ok: rug.freezeRevoked });
    } else {
      out.push({ k: "On-chain safety", v: "unverified — DYOR", ok: false });
    }
    return out;
  };

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    ctx.strokeStyle = "rgba(0,229,153,0.30)";
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // wordmark
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.font = "800 40px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("ALPHA", 70, 110);
    const w1 = ctx.measureText("ALPHA").width;
    ctx.fillStyle = NEON;
    ctx.fillText("APEING", 70 + w1, 110);

    // headline
    ctx.fillStyle = WARN;
    ctx.font = "700 30px monospace";
    ctx.fillText("🔬 TOKEN AUTOPSY", 70, 180);
    ctx.fillStyle = "#fff";
    ctx.font = "800 100px Arial, sans-serif";
    ctx.fillText(`$${(coin.symbol || "").replace("$", "").slice(0, 12)}`, 66, 285);
    ctx.fillStyle = MUTED;
    ctx.font = "400 30px Arial, sans-serif";
    ctx.fillText((coin.name || "").slice(0, 34), 70, 330);

    // score
    ctx.fillStyle = MUTED;
    ctx.font = "600 24px monospace";
    ctx.fillText("DEGEN SCORE", 70, 420);
    ctx.fillStyle = verdictColor(coin.score.verdict);
    ctx.font = "800 130px Arial, sans-serif";
    ctx.fillText(String(coin.score.total), 66, 540);
    const sw = ctx.measureText(String(coin.score.total)).width;
    ctx.font = "700 40px Arial, sans-serif";
    ctx.fillStyle = MUTED;
    ctx.fillText(`/${max}`, 66 + sw + 14, 540);
    ctx.fillStyle = verdictColor(coin.score.verdict);
    ctx.font = "700 34px monospace";
    ctx.fillText(coin.score.verdict, 70, 585);

    // forensic lines
    let y = 660;
    ctx.font = "600 30px monospace";
    lines().forEach((ln) => {
      ctx.fillStyle = MUTED;
      ctx.fillText(`${ln.k}:`, 70, y);
      const kw = ctx.measureText(`${ln.k}: `).width;
      ctx.fillStyle = ln.ok ? NEON : DANGER;
      ctx.fillText(ln.v, 70 + kw, y);
      y += 52;
    });

    // observed + report
    ctx.fillStyle = MUTED;
    ctx.font = "400 26px monospace";
    ctx.fillText(`Data observed: ${utcTime(coin.score.scoredAt)}`, 70, 960);

    ctx.fillStyle = NEON;
    ctx.font = "700 30px monospace";
    ctx.fillText("Full report: alphaapeing.com", 70, 1015);
    ctx.textAlign = "right";
    ctx.fillStyle = MUTED;
    ctx.font = "400 22px Arial, sans-serif";
    ctx.fillText("Research tool · not financial advice", W - 70, 1015);
    ctx.textAlign = "left";
    stampBrandApe(c);
  }, [open, coin, rugQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const download = async () => {
    const url = URL.createObjectURL(await getBlob());
    const a = document.createElement("a");
    a.href = url; a.download = `autopsy-${coin.symbol}.png`; a.click();
    URL.revokeObjectURL(url);
    trackEvent("share", getClientId(), { mint: coin.address, via: "download" });
    toast.success("Autopsy card downloaded");
  };
  const copy = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": await getBlob() })]);
      trackEvent("share", getClientId(), { mint: coin.address, via: "copy" });
      toast.success("Image copied — paste it into your post");
    } catch { toast.error("Copy not supported here — use Download instead"); }
    setCopying(false);
  };
  const shareX = () => { trackEvent("share", getClientId(), { mint: coin.address, via: "x" }); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank"); };
  const shareTg = () => { trackEvent("share", getClientId(), { mint: coin.address, via: "telegram" }); window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank"); };

  return createPortal(
    <div data-testid="autopsy-card-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-md w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">🔬 Token Autopsy: {coin.symbol}</h3>
          <button data-testid="autopsy-close-btn" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>
        <div className="border border-white/10 overflow-hidden">
          <canvas ref={canvasRef} width={1080} height={1080} className="w-full h-auto block" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="autopsy-download-btn" onClick={download} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            <DownloadSimpleIcon size={16} /> Download
          </button>
          <button data-testid="autopsy-copy-btn" onClick={copy} disabled={copying} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-50">
            {copying ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CopyIcon size={16} />} Copy image
          </button>
          <button data-testid="autopsy-x-btn" onClick={shareX} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
            <XLogoIcon size={16} weight="fill" /> Share on X
          </button>
          <button data-testid="autopsy-tg-btn" onClick={shareTg} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#229ED9] text-white text-sm font-semibold hover:bg-[#229ED9]/90 transition-colors">
            <TelegramLogoIcon size={16} weight="fill" /> Telegram
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
